import React, { useState } from "react";
import { Button } from "react-bootstrap";
import PhotoGalleryService from "../../../_services/PhotoGalleryService";

interface PhotoTag {
  entityType: string;
  entityId: number;
  entityName?: string;
}

interface Photo {
  id: number;
  s3Url: string;
  thumbnailUrl: string | null;
  originalFilename: string | null;
  caption: string | null;
  tags?: PhotoTag[];
}

interface Props {
  photos: Photo[];
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDelete: (photo: Photo) => void;
  onViewPhoto: (photo: Photo) => void;
  onTagRemoved?: () => void;
}

const TAG_TYPE_COLORS: Record<string, string> = {
  STUDENT: "#0d6efd",
  BATCH: "#6f42c1",
  COURSE: "#0dcaf0",
  PROGRAM: "#198754",
  SCHOOL: "#fd7e14",
  YEAR: "#dc3545",
};

const TAG_TYPE_LABELS: Record<string, string> = {
  STUDENT: "Student",
  BATCH: "Batch",
  COURSE: "Course",
  PROGRAM: "Program",
  SCHOOL: "School",
  YEAR: "Year",
};

const PhotoGrid: React.FC<Props> = ({
  photos,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onDelete,
  onViewPhoto,
  onTagRemoved,
}) => {
  const [expandedPhotoId, setExpandedPhotoId] = useState<number | null>(null);
  const [editingCaptionId, setEditingCaptionId] = useState<number | null>(null);
  const [captionText, setCaptionText] = useState("");

  const handleCaptionSave = async (photoId: number) => {
    setEditingCaptionId(null);
    try {
      await PhotoGalleryService.updatePhoto(photoId, { caption: captionText });
      onTagRemoved?.(); // reload photos
    } catch (err) {
      console.error("Failed to save caption", err);
    }
  };

  const handleRemoveTag = async (
    photoId: number,
    entityType: string,
    entityId: number,
  ) => {
    try {
      await PhotoGalleryService.untagPhoto(photoId, entityType, entityId);
      onTagRemoved?.();
    } catch (err) {
      console.error("Failed to remove tag", err);
    }
  };

  if (photos.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        No photos in this album yet. Upload some photos above.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <Button variant="secondary" size="sm" onClick={onSelectAll}>
          Select All
        </Button>
        <Button variant="secondary" size="sm" onClick={onDeselectAll}>
          Deselect All
        </Button>
        <span className="text-muted" style={{ fontSize: 13 }}>
          {selectedIds.size} selected
        </span>
      </div>
      <div className="photo-grid">
        {photos.map((photo) => {
          const tags = photo.tags || [];
          const isExpanded = expandedPhotoId === photo.id;

          return (
            <div
              key={photo.id}
              className={`photo-grid-item ${selectedIds.has(photo.id) ? "selected" : ""}`}
            >
              <div className="photo-img-wrapper">
                <input
                  type="checkbox"
                  className="photo-checkbox"
                  checked={selectedIds.has(photo.id)}
                  onChange={() => onToggleSelect(photo.id)}
                />
                <button
                  className="photo-delete-btn"
                  title="Delete photo"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(photo);
                  }}
                >
                  x
                </button>
                <img
                  src={photo.thumbnailUrl || photo.s3Url}
                  alt={photo.originalFilename || ""}
                  onClick={() => onViewPhoto(photo)}
                  loading="lazy"
                />
                {/* Tag count badge */}
                {tags.length > 0 && (
                  <button
                    className="photo-tag-badge"
                    title="View tags"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedPhotoId(isExpanded ? null : photo.id);
                    }}
                  >
                    <i className="fa fa-tag" /> {tags.length}
                  </button>
                )}
                {/* Tag detail popover */}
                {isExpanded && tags.length > 0 && (
                  <div
                    className="photo-tag-popover"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="photo-tag-popover-header">
                      <span>Tags</span>
                      <button onClick={() => setExpandedPhotoId(null)}>x</button>
                    </div>
                    {tags.map((tag, i) => (
                      <div key={i} className="photo-tag-popover-item">
                        <span
                          className="photo-tag-type-dot"
                          style={{ background: TAG_TYPE_COLORS[tag.entityType] || "#6c757d" }}
                        />
                        <span className="photo-tag-popover-label">
                          <span style={{ color: "#868e96", fontSize: 10 }}>
                            {TAG_TYPE_LABELS[tag.entityType] || tag.entityType}
                          </span>
                          <br />
                          {tag.entityName || `#${tag.entityId}`}
                        </span>
                        <button
                          className="photo-tag-remove-btn"
                          title="Remove tag"
                          onClick={() =>
                            handleRemoveTag(photo.id, tag.entityType, tag.entityId)
                          }
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Caption */}
              <div className="photo-caption-area" onClick={(e) => e.stopPropagation()}>
                {editingCaptionId === photo.id ? (
                  <input
                    type="text"
                    className="photo-caption-input"
                    value={captionText}
                    onChange={(e) => setCaptionText(e.target.value)}
                    onBlur={() => handleCaptionSave(photo.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCaptionSave(photo.id);
                      if (e.key === "Escape") setEditingCaptionId(null);
                    }}
                    autoFocus
                    placeholder="Add caption..."
                  />
                ) : (
                  <div
                    className="photo-caption-text"
                    onClick={() => {
                      setEditingCaptionId(photo.id);
                      setCaptionText(photo.caption || "");
                    }}
                    title="Click to edit caption"
                  >
                    {photo.caption || (
                      <span style={{ color: "var(--text-faint)", fontStyle: "italic" }}>
                        Add caption...
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PhotoGrid;
